/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { useDisplayRfFeedData } from "./hooks";
import { useStoreDispatch } from "../../../store/hooks";
import "../Config.css";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { useSelector } from "react-redux";
import { customDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import AddEditRfFeed from "../../../components/Configuration/RfFeed/AddEditRfFeed";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { IRfFeed } from "../../../store/api/deviceConfig/types";
import { setOpenCustomDailog, setConfirmDialog } from "../../../store/helpers/helperReducers";
import { setIsAddNewRfFeed, setSelectedRfFeed } from "../../../store/api/deviceConfig/reducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { constStr } from "../../../globals/constants";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { noOfSelectedRowSelector } from "../../../store/api/devices/selectors";
import { setNoOfSelectedRow } from "../../../store/api/devices/reducers";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { Plus } from "lucide-react";

const RfFeeds: React.FC = () => {
  const dispatch = useStoreDispatch();
  const { data, isLoading, permissions, RfFeedsColumns, selectedRfFeed, deleteRfFeedHandler } = useDisplayRfFeedData();

  //const getDataFromApi = data?.data || [];
  const rowsData = data || [];
  //const rowsData = prepareRfFeedDataGrid(data) || [];
  //const [NoOfSelectedRow, setNoOfSelectedRow] = React.useState(0);
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");

  const openCustomDialog = useSelector(customDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const { rfFeedsRowIds = [] } = useSelector(noOfSelectedRowSelector);

  const handleOnAddRfFeed = (event: any) => {
    setModelOpenMode("Add");
    dispatch(setIsAddNewRfFeed(true));
    dispatch(setOpenCustomDailog(true));
  };

  const handleOnEdit = (event: any) => {
    setModelOpenMode("Edit");
    dispatch(setIsAddNewRfFeed(false));
    dispatch(setOpenCustomDailog(true));
  };

  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    const rowIdLength = rowIds?.length;
    dispatch(setNoOfSelectedRow({ rfFeedsRowIds: rowIds }));
    //setNoOfSelectedRow(rowIdLength);
    if (rowIdLength === 1) {
      dispatch(setSelectedRfFeed(rowsData.filter((item) => item.index === rowIds[0])[0]));
    }
  };

  const handleOnDelete = (event: any) => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.RFFEED));
  };

  return (
    <>
      {openCustomDialog && <AddEditRfFeed modelOpenMode={modelOpenMode} selectedRfFeed={selectedRfFeed} />}
      {openConfirmDialog === DIALOGOPENFOR.RFFEED && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteRfFeedHandler}>
          {`Are you sure you want to delete RF FEED ${selectedRfFeed.index}?`}
        </ConfirmDialog>
      )}
      <div>
        {rfFeedsRowIds?.length > 0 ? (
          <div className="pb-4">
            <div className="flex gap-2 items-center">
              <p className="text-sm text-foreground pr-2">
                <strong>{rfFeedsRowIds?.length} row selected</strong>
              </p>
              {permissions?.hasOpCodeGroup("manage_topology") && rfFeedsRowIds?.length === 1 && (
                <>
                  <Button variant="secondary" size="sm" onClick={handleOnEdit}
                  >
                    Edit
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleOnDelete}>
                    <Icon name="delete" size={16} />
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <></>
        )}
        <div
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius)",
            padding: "18px",
            boxShadow: "var(--shadow-card)",
            backgroundColor: "var(--card)",
          }}
        >
          {isLoading && <p>Loading...</p>}
          {!isLoading && (
            <CustomDataGrid
              tableId="rfFeed"
              rows={rowsData}
              columns={RfFeedsColumns}
              checkboxSelection={true}
              loading={isLoading}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              onRowSelectionModelChange={handleOnRowSelected}
              rowSelectionModel={rfFeedsRowIds}
              getRowId={(row: IRfFeed) => row.index!}
              sx={{
                "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
                "& .MuiDataGrid-scrollbar--vertical": { display: "none" },
              }}
              fillContainer={false}
            />
          )}
        </div>
        {permissions?.hasOpCodeGroup("manage_topology") && (
          <div className="pt-4 pb-4">
            <Button
              variant="outline"
              className="h-10 px-4 text-sm font-medium border border-input bg-background shadow-sm flex items-center gap-2 hover:bg-muted hover:text-accent-foreground transition-colors"
              onClick={handleOnAddRfFeed}
            >
              <Plus className="h-4 w-4" />
              Add RF Feed
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
export default RfFeeds;
